// The score bar's need for the shortlist's reading: the bar is visible on
// the active board while the best moves are hidden, so the panel is not
// mounted to hold the reading itself. Pure: no React, no DOM.
import type { ConstraintRegistry, ConstraintScope } from '@chess/api-constraints';

type Registry = Pick<ConstraintRegistry, 'current' | 'subscribe'>;

/** Whether the score bar needs the reading in this scope. */
export function scoreBarNeedsReading(registry: Pick<ConstraintRegistry, 'current'>, scope: ConstraintScope | undefined): boolean {
  return !!scope && registry.current(scope, 'scoreBarVisibility').visible && !registry.current(scope, 'bestMovesVisibility').visible;
}

export interface NeedWatch {
  /** Follow another scope (the active board's), or none. */
  watch(scope: ConstraintScope | undefined): void;
  /** Stop following and let go of the reading. */
  dispose(): void;
}

/**
 * Holds the reading (`acquire`) while the score bar needs it in the scope
 * being watched, and lets it go when it no longer does.
 */
export function watchNeed(registry: Registry, acquire: () => () => void): NeedWatch {
  let scope: ConstraintScope | undefined;
  let offConstraints: (() => void) | undefined;
  let release: (() => void) | undefined;
  const decide = () => {
    const needed = scoreBarNeedsReading(registry, scope);
    if (needed && !release) release = acquire();
    if (!needed && release) {
      release();
      release = undefined;
    }
  };
  return {
    watch(next) {
      if (next === scope) return;
      offConstraints?.();
      scope = next;
      if (scope) {
        const a = registry.subscribe(scope, 'scoreBarVisibility', decide);
        const b = registry.subscribe(scope, 'bestMovesVisibility', decide);
        offConstraints = () => {
          a();
          b();
        };
      } else {
        offConstraints = undefined;
      }
      decide();
    },
    dispose() {
      offConstraints?.();
      offConstraints = undefined;
      scope = undefined;
      release?.();
      release = undefined;
    },
  };
}
